import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, ArrowRight, User } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const ProfileSetupPage = () => {
  const { user, axiosInstance, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [displayName, setDisplayName] = useState(user?.display_name || user?.username || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
      if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!displayName.trim()) { toast.error('Please enter a display name'); return; }
    setSaving(true);
    try {
      // Upload avatar if selected
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);
        await axiosInstance.post('/profile/avatar', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      // Update profile
      const params = new URLSearchParams({ display_name: displayName.trim(), bio: bio.trim() });
      await axiosInstance.put(`/profile/update?${params.toString()}`);

      if (refreshUser) await refreshUser();
      toast.success('Profile set up!');
      // Resume where the user was before they hit signup, if applicable.
      let next = '/';
      try {
        const stashed = sessionStorage.getItem('aivue.postAuthNext');
        if (stashed && stashed.startsWith('/')) {
          next = stashed;
          sessionStorage.removeItem('aivue.postAuthNext');
        }
      } catch { /* noop */ }
      navigate(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const getInitial = () => (displayName || user?.username || '?').charAt(0).toUpperCase();

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: '#050505' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Set up your profile</h1>
          <p className="text-zinc-400 text-sm">This is how other creators and viewers will see you</p>
        </div>

        {/* Avatar */}
        <div className="flex justify-center mb-8">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="relative group"
            data-testid="avatar-upload-btn"
          >
            <div className="w-28 h-28 rounded-full overflow-hidden bg-zinc-800 flex items-center justify-center">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-4xl font-bold text-zinc-500">{getInitial()}</span>
              )}
            </div>
            <div className="absolute inset-0 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <Camera className="w-7 h-7 text-white" />
            </div>
          </button>
          <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarSelect} className="hidden" data-testid="avatar-file-input" />
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="displayName" className="text-zinc-300">Display Name *</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your artist or display name"
              className="bg-zinc-900 border-zinc-800 text-white focus:border-white"
              data-testid="display-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio" className="text-zinc-300">Bio</Label>
            <textarea
              id="bio"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell people about yourself..."
              rows={3}
              maxLength={200}
              className="w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-4 py-2 text-sm focus:border-white focus:ring-1 focus:ring-white/20 outline-none resize-none"
              data-testid="bio-input"
            />
            <p className="text-xs text-zinc-600 text-right">{bio.length}/200</p>
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || !displayName.trim()}
            className="w-full bg-white hover:bg-zinc-200 text-black font-semibold py-3 rounded-full"
            data-testid="save-profile-btn"
          >
            {saving ? 'Saving...' : 'Continue'}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>

          <button
            onClick={() => {
              let next = '/';
              try {
                const stashed = sessionStorage.getItem('aivue.postAuthNext');
                if (stashed && stashed.startsWith('/')) {
                  next = stashed;
                  sessionStorage.removeItem('aivue.postAuthNext');
                }
              } catch { /* noop */ }
              navigate(next);
            }}
            className="w-full py-2 text-sm text-zinc-500 hover:text-zinc-300 transition-colors"
            data-testid="skip-btn"
          >
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
};
